import { useMemo, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar, Hash, Users, Scale, CheckCircle2, Eye, Sparkles, HelpCircle, Download, Image as ImageIcon, Share2, Link as LinkIcon, MessageCircle, BookText, Gavel, FileText, ChevronDown, ChevronUp, ShieldCheck, HeartHandshake, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { JudgmentCarousel } from "@/components/JudgmentCarousel";
import { LanguageToggle } from "@/components/LanguageToggle";
import { InfographicCard } from "@/components/InfographicCard";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import Seo from "@/components/Seo";
import { articleJsonLd, breadcrumbJsonLd, judgmentMetaTitle, judgmentMetaDescription, caseYear, faqJsonLd } from "@/lib/seo";
import imageUrl from "@/lib/imageUrl";
import { PRACTICE_AREA_LIST } from "@/lib/practiceAreaContent";
import { toast } from "sonner";

// Map judgment subject tokens -> relevant Supreme Court practice-area slugs so a
// reader on a judgment is pointed to the right lawyer page (internal linking +
// "recommend similar content" signal).
const PRACTICE_MATCHERS = [
  [/anticipat|bail/i, "anticipatory-bail"],
  [/\bfir\b|quash/i, "quashing-of-fir"],
  [/murder|302|conviction|criminal appeal/i, "murder-conviction-appeal"],
  [/pmla|money laundering|enforcement directorate|\bed\b/i, "pmla-money-laundering"],
  [/ndps|narcotic|drug/i, "ndps-act-drug-offences-appeal"],
  [/pocso|child sexual/i, "pocso-act-conviction-appeal"],
  [/uapa|terror/i, "uapa-anti-terror-bail-slp"],
  [/pension/i, "pension-recovery-disputes"],
  [/service|promotion|regulariz|employ|termination/i, "service-matters-promotion-appeals"],
  [/constitution|writ|article 32|fundamental right/i, "article-32-writ-petition"],
  [/cheque|138|dishonour/i, "section-138-cheque-bounce"],
  [/arbitrat/i, "section-11-international-arbitration"],
  [/insolvency|ibc|nclat/i, "nclat-insolvency-ibc"],
  [/sarfaesi|drat/i, "sarfaesi-drat-property-appeal"],
  [/consumer/i, "ncdrc-consumer-rights-appeal"],
  [/environment|pollution|ngt/i, "ngt-environmental-clearance-appeal"],
  [/\btax\b|gst|customs|income tax/i, "itat-cestat-tax-appeal"],
  [/land acquisition|compensation/i, "land-acquisition-compensation"],
  [/partition|ancestral|property|succession|inherit/i, "ancestral-property-partition"],
  [/custody|matrimon|divorce|maintenance|family/i, "child-custody-transfer"],
  [/transfer petition/i, "matrimonial-transfer-petition"],
  [/review/i, "review-petition"],
  [/slp|special leave|article 136/i, "slp"],
];

function relatedPractices(j) {
  const hay = [j.area_of_law, ...(j.keywords || []), j.case_title, j.one_line_en]
    .filter(Boolean).join(" ").toLowerCase();
  const slugs = [];
  for (const [rx, slug] of PRACTICE_MATCHERS) {
    if (rx.test(hay) && !slugs.includes(slug)) slugs.push(slug);
    if (slugs.length >= 3) break;
  }
  return slugs
    .map((s) => PRACTICE_AREA_LIST.find((p) => p.slug === s))
    .filter(Boolean)
    .slice(0, 3);
}

function Section({ title, children, testId }) {
  if (!children) return null;
  return (
    <div className="mt-8" data-testid={testId}>
      <h2 className="font-serif text-xl font-bold tracking-tight">{title}</h2>
      <div className="mt-2 reading-prose text-foreground/90 text-justify"><p>{children}</p></div>
    </div>
  );
}

/** E-E-A-T byline: "Summarized by [Editor] | Verified by [Advocate], Supreme Court of India" — links to /team. */
function Byline({ lang }) {
  const { data: team } = useQuery({
    queryKey: ["team", "byline"],
    queryFn: async () => (await api.get("/public/team")).data,
    staleTime: 5 * 60 * 1000,
  });
  const { data: lawyers } = useQuery({
    queryKey: ["lawyers", "byline"],
    queryFn: async () => (await api.get("/public/lawyers")).data,
    staleTime: 5 * 60 * 1000,
  });
  const author = (team || []).find((t) => t.is_founder) || (team || [])[0];
  const verifier = (lawyers || []).find((l) => l.is_founder) || (lawyers || [])[0];
  if (!author && !verifier) return null;
  return (
    <Link
      to="/team"
      data-testid="judgment-byline"
      className="inline-flex flex-wrap items-center gap-1.5 text-xs font-medium text-[hsl(160_60%_32%)] hover:underline"
    >
      <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
      {author && <span>{lang === "hi" ? "सारांश" : "Summarized by"} {author.name}</span>}
      {author && verifier && <span className="text-muted-foreground">|</span>}
      {verifier && (
        <span>
          {lang === "hi" ? "सत्यापित" : "Verified by"} {verifier.name}, {lang === "hi" ? "भारत का सर्वोच्च न्यायालय" : "Supreme Court of India"}
        </span>
      )}
    </Link>
  );
}

/** 3-5 Related Questions of Law from the same legal category, plus matching advocates. */
function RelatedQuestionsOfLaw({ j, lang }) {
  const { data } = useQuery({
    queryKey: ["related-judgments", j.area_of_law, j.id],
    queryFn: async () => (await api.get(`/public/judgments?area=${encodeURIComponent(j.area_of_law || "")}&limit=6`)).data,
    enabled: !!j.area_of_law,
  });
  const { data: lawyers } = useQuery({
    queryKey: ["lawyers", "related-advocates"],
    queryFn: async () => (await api.get("/public/lawyers")).data,
  });

  const related = (data?.items || []).filter((x) => x.id !== j.id).slice(0, 5);
  const areaWord = (j.area_of_law || "").toLowerCase().split(/[\s&]+/)[0];
  const matchingLawyers = (lawyers || [])
    .filter((lw) => {
      if (!areaWord) return false;
      const hay = `${lw.specialization_en || ""} ${(lw.helps_en || []).join(" ")}`.toLowerCase();
      return hay.includes(areaWord);
    })
    .slice(0, 3);

  if (!related.length && !matchingLawyers.length) return null;

  return (
    <section className="mt-12 border-t pt-10" data-testid="related-questions-section">
      {related.length > 0 && (
        <div>
          <h2 className="flex items-center gap-2 font-serif text-xl font-bold">
            <HelpCircle className="h-5 w-5 text-primary" />
            {lang === "hi" ? "संबंधित कानूनी सवाल" : "Related Questions of Law"}
          </h2>
          <ul className="mt-4 space-y-2" data-testid="related-questions-list">
            {related.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/judgments/${r.slug || r.id}`}
                  className="flex items-start gap-2 rounded-xl border bg-card p-3 text-sm font-medium transition-colors hover:border-primary/40 hover:text-primary"
                  data-testid={`related-question-${r.id}`}
                >
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {r.headline_question_en || r.plain_title_en || r.case_title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {matchingLawyers.length > 0 && (
        <div className="mt-8">
          <h3 className="font-serif text-lg font-bold">
            {lang === "hi" ? `${j.area_of_law} में अधिवक्ता` : `Advocates handling ${j.area_of_law}`}
          </h3>
          <div className="mt-3 flex flex-wrap gap-3" data-testid="related-advocates-list">
            {matchingLawyers.map((lw) => (
              <Link
                key={lw.id}
                to={`/supreme-court-lawyers/${lw.id}`}
                className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-sm font-medium transition-colors hover:border-primary/40 hover:text-primary"
                data-testid={`related-advocate-${lw.id}`}
              >
                <Scale className="h-3.5 w-3.5 text-primary" /> {lw.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function ShareBar({ j }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? `${window.location.origin}/judgments/${j.slug || j.id}` : "";
  const text = j.one_line_en ? `${j.case_title}: ${j.one_line_en}` : j.case_title;

  const share = (platform) => {
    const e = encodeURIComponent;
    const links = {
      whatsapp: `https://api.whatsapp.com/send?text=${e(text + " — Read more: " + url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${e(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${e(url)}`,
    };
    window.open(links[platform], "_blank", "width=620,height=460,noopener,noreferrer");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied!");
    } catch {
      toast.info(url);
    }
  };

  return (
    <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="share-bar">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Share:</span>
      <Button
        variant="outline" size="sm"
        onClick={() => share("whatsapp")}
        className="gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950"
        data-testid="share-whatsapp"
      >
        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
      </Button>
      <Button
        variant="outline" size="sm"
        onClick={() => share("facebook")}
        className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950"
        data-testid="share-facebook"
      >
        <Share2 className="h-3.5 w-3.5" /> Facebook
      </Button>
      <Button
        variant="outline" size="sm"
        onClick={() => share("linkedin")}
        className="gap-1.5 border-sky-200 text-sky-700 hover:bg-sky-50 dark:border-sky-800 dark:text-sky-400 dark:hover:bg-sky-950"
        data-testid="share-linkedin"
      >
        <Share2 className="h-3.5 w-3.5" /> LinkedIn
      </Button>
      <Button
        variant="outline" size="sm"
        onClick={copyLink}
        className="gap-1.5"
        data-testid="share-copy"
      >
        {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <LinkIcon className="h-3.5 w-3.5" />}
        {copied ? "Copied!" : "Copy link"}
      </Button>
    </div>
  );
}

function FullText({ text, caseTitle, lang }) {
  const [open, setOpen] = useState(false);

  // Clean PDF extraction artifacts: mid-word hyphen breaks, excessive whitespace
  const clean = text
    .replace(/\r/g, "")
    .replace(/\u00AD/g, "")                              // soft hyphen
    .replace(/-\n(?=[a-z])/g, "")                       // hyphenated line breaks
    .replace(/([^\n])\n(?!\n)(?=[A-Za-z0-9\u0900-\u097F])/g, "$1 ") // single newline → space
    .replace(/ {2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n");

  const paragraphs = clean.split(/\n{2,}/).map((p) => p.trim()).filter((p) => p.length > 10);

  return (
    <section className="mt-10" data-testid="judgment-full-text" itemProp="articleBody">
      <h2 className="flex items-center gap-2 font-serif text-xl font-bold">
        <FileText className="h-5 w-5 text-primary" />
        {lang === "hi" ? "फैसले का पूरा पाठ" : "Full text of the judgment"}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {lang === "hi"
          ? "यह पाठ मूल निर्णय की प्रति से निकाला गया है। आधिकारिक संदर्भ के लिए सर्वोच्च न्यायालय की वेबसाइट देखें।"
          : `Text extracted from ${caseTitle}. For the authoritative version refer to the Supreme Court of India's official website.`}
      </p>
      <div className={`relative mt-4 rounded-2xl border bg-card px-5 py-5 sm:px-8 ${open ? "" : "max-h-[28rem] overflow-hidden"}`}>
        <div className="space-y-4 font-serif text-[15px] leading-[1.85] text-foreground/90">
          {paragraphs.map((p, i) => (
            <p key={i} className="text-justify hyphens-auto">{p}</p>
          ))}
        </div>
        {!open && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-card to-transparent" />
        )}
      </div>
      <Button
        variant="outline" size="sm"
        className="mt-3 gap-1.5"
        onClick={() => setOpen((o) => !o)}
        data-testid="toggle-full-text"
      >
        {open
          ? <><ChevronUp className="h-4 w-4" /> {lang === "hi" ? "कम दिखाएँ" : "Collapse"}</>
          : <><ChevronDown className="h-4 w-4" /> {lang === "hi" ? "पूरा पाठ पढ़ें" : "Read the complete judgment text"}</>}
      </Button>
    </section>
  );
}

export default function JudgmentDetail() {
  const { slug } = useParams();
  const { t, L, lang } = useLanguage();
  const { data: j, isLoading, isError } = useQuery({
    queryKey: ["judgment", slug],
    queryFn: async () => (await api.get(`/public/judgments/${slug}`)).data,
  });

  // Scroll to top on navigation to any judgment (critical for mobile UX / fast open from search results)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const year = j ? caseYear(j) : "";
  const seoKeywords = useMemo(
    () => [
      j?.case_title,
      year ? `${j?.case_title} ${year}` : "",
      ...(j?.keywords || []),
      ...((j?.relevant_provisions || []).map((p) => (p.sections ? `${p.sections}, ${p.act_name}` : p.act_name))),
      "Supreme Court judgment",
      j?.area_of_law,
    ].filter(Boolean).join(", "),
    [j?.case_title, year, j?.keywords, j?.relevant_provisions, j?.area_of_law]
  );

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="mt-4 h-64 w-full rounded-2xl" />
      </div>
    );
  }
  if (isError || !j) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-24 text-center">
        <p className="text-muted-foreground">{t("no_results")}</p>
        <Button asChild className="mt-4"><Link to="/updates">{t("view_all_updates")}</Link></Button>
      </div>
    );
  }

  const summary = L(j, "summary");
  const oneLine = L(j, "one_line");
  const headlineQ = L(j, "headline_question");
  const meaning = lang === "hi" ? j.meaning_hi : j.meaning_en;
  const plainTitle = lang === "hi" ? (j.plain_title_hi || j.case_title) : (j.plain_title_en || j.case_title);
  const metaDesc = judgmentMetaDescription(j);
  const infographicSrc = imageUrl(j.infographic_image, { w: 1200 });

  // ── Question-first structure (TASK 3) ──
  // h1 = Lead Question of Law. h2 = Case Ruling Headline (plain-language title).
  const leadQuestion = headlineQ || plainTitle || j.case_title;
  const rulingHeadline = plainTitle && plainTitle !== leadQuestion ? plainTitle : (oneLine && oneLine !== leadQuestion ? oneLine : "");

  // FAQPage schema from this judgment's own headline question + plain-language
  // answer — lets Google match and surface this exact page when a user's
  // search query matches the question or the judgment summary text.
  const judgmentHeadlineQ = j.headline_question_en || j.headline_question_hi;
  const judgmentFaqAnswer = j.meaning_en || j.one_line_en || j.summary_en;
  const judgmentFaqJsonLd = judgmentHeadlineQ && judgmentFaqAnswer
    ? faqJsonLd([{ q_en: j.headline_question_en || j.headline_question_hi, a_en: judgmentFaqAnswer }])
    : null;

  return (
    <div>
      <Seo
        title={judgmentMetaTitle(j)}
        description={metaDesc}
        keywords={seoKeywords}
        path={`/judgments/${j.slug || j.id}`}
        image={infographicSrc || j.cover_image || undefined}
        type="article"
        jsonLd={[
          articleJsonLd(j),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Updates", path: "/updates" },
            { name: j.case_title, path: `/judgments/${j.slug || j.id}` },
          ]),
          judgmentFaqJsonLd,
        ].filter(Boolean)}
      />
      <div className="li-hero-gradient border-b">
        <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
          <Link to="/updates" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-updates">
            <ArrowLeft className="h-4 w-4" /> {t("nav_updates")}
          </Link>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {j.area_of_law && <Badge variant="secondary" className="bg-accent text-accent-foreground">{j.area_of_law}</Badge>}
            <Byline lang={lang} />
            <span className="flex items-center gap-1 text-xs text-muted-foreground"><Eye className="h-3.5 w-3.5" /> {j.views} {t("views")}</span>
          </div>
          <div className="mt-3 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div className="max-w-3xl">
              {/* H1 — Lead Question of Law */}
              <h1 data-testid="judgment-title" className="font-serif text-3xl font-bold leading-tight tracking-tight sm:text-4xl">{leadQuestion}</h1>
              {/* H2 — Case Ruling Headline */}
              {rulingHeadline && (
                <h2 className="mt-2 text-lg font-semibold text-muted-foreground" data-testid="judgment-ruling-headline">{rulingHeadline}</h2>
              )}
            </div>
            <LanguageToggle />
          </div>
          {/* Subtitle bar: Case | Citation | Decided on */}
          <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs text-muted-foreground" data-testid="judgment-subtitle-bar">
            <span>{lang === "hi" ? "मामला" : "Case"}: {j.case_title}</span>
            {j.citation && <><span aria-hidden>|</span><span>{lang === "hi" ? "उद्धरण" : "Citation"}: {j.citation}</span></>}
            {j.judgment_date && <><span aria-hidden>|</span><span>{lang === "hi" ? "निर्णय तिथि" : "Decided on"}: {j.judgment_date}</span></>}
          </p>
          {/* What this case means — 2-3 line plain-language summary */}
          {meaning && (
            <div className="mt-4 max-w-3xl rounded-xl border border-teal-200 bg-teal-50 p-4 dark:border-teal-800 dark:bg-teal-950/30" data-testid="judgment-meaning">
              <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-400">
                {lang === "hi" ? "यह मामला क्या था" : "What this case means"}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-foreground/90">{meaning}</p>
            </div>
          )}
          {oneLine && (
            <p className="mt-4 max-w-3xl rounded-lg border-l-4 border-primary bg-card p-4 text-base font-medium italic text-foreground/90">
              &ldquo;{oneLine}&rdquo;
            </p>
          )}
          {/* Share bar placed prominently in hero */}
          <ShareBar j={j} />
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_300px]">
        <div className="min-w-0">
          {/* Slides */}
          {j.slides?.length > 0 && (
            <div>
              <h2 className="mb-3 flex items-center gap-2 font-serif text-xl font-bold"><Sparkles className="h-5 w-5 text-primary" /> {t("slides")}</h2>
              <JudgmentCarousel slides={j.slides} caseTitle={j.case_title} citation={j.citation} date={j.judgment_date} />
            </div>
          )}

          {/* "At a glance" summary */}
          {(j.infographic || infographicSrc) && (
            <div className="mt-10" data-testid="judgment-infographic">
              <h2 className="mb-3 flex items-center gap-2 font-serif text-xl font-bold">
                <ImageIcon className="h-5 w-5 text-primary" />
                {lang === "hi" ? "एक नज़र में (इन्फोग्राफिक)" : "At a glance (infographic)"}
              </h2>
              {j.infographic ? (
                <InfographicCard
                  infographic={j.infographic}
                  caseTitle={j.case_title}
                  citation={j.citation}
                  date={j.judgment_date}
                />
              ) : (
                <>
                  <div className="overflow-hidden rounded-2xl border bg-card">
                    <img
                      src={infographicSrc}
                      alt={`${j.case_title} — infographic summary`}
                      loading="lazy"
                      decoding="async"
                      width={1024}
                      height={1024}
                      className="mx-auto aspect-square w-full max-w-2xl object-contain"
                    />
                  </div>
                  <a
                    href={infographicSrc}
                    download={`${j.slug || "judgment"}-infographic.webp`}
                    className="mt-3 inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                    data-testid="download-infographic"
                  >
                    <Download className="h-4 w-4" /> {lang === "hi" ? "इन्फोग्राफिक डाउनलोड करें" : "Download infographic"}
                  </a>
                </>
              )}
            </div>
          )}

          <Section title={t("in_short")} testId="judgment-summary-section">{summary}</Section>

          {/* Callout — directly below the judgment summary */}
          <div className="mt-6 flex flex-col items-start gap-4 rounded-2xl border border-primary/30 bg-primary/5 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="similar-issue-callout">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><HeartHandshake className="h-5 w-5" /></span>
              <div>
                <p className="font-serif text-base font-bold leading-snug">
                  {lang === "hi" ? "किसी समान कानूनी मुद्दे पर स्पष्टता चाहिए?" : "Need clarity on a similar legal issue?"}
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {lang === "hi" ? "सुप्रीम कोर्ट अधिवक्ता से निःशुल्क प्रो बोनो मार्गदर्शन का अनुरोध करें।" : "Request free pro bono guidance from a Supreme Court Advocate."}
                </p>
              </div>
            </div>
            <Button asChild className="grad-cta shrink-0 text-white" data-testid="similar-issue-cta">
              <Link to="/advice">{lang === "hi" ? "मार्गदर्शन का अनुरोध करें" : "Request Guidance"} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>

          <Section title={t("background")}>{L(j, "background")}</Section>
          <Section title={t("the_decision")}>{L(j, "decision")}</Section>
          <Section title={t("why_it_matters")}>{L(j, "why_it_matters")}</Section>

          {/* Relevant Provisions */}
          {j.relevant_provisions?.length > 0 && (
            <div className="mt-8" data-testid="judgment-provisions">
              <h2 className="flex items-center gap-2 font-serif text-xl font-bold">
                <BookText className="h-5 w-5 text-primary" />
                {lang === "hi" ? "प्रासंगिक कानूनी प्रावधान" : "Relevant Legal Provisions"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {lang === "hi" ? "इस फैसले में उल्लिखित मुख्य कानून और धाराएं" : "Key Acts and sections cited or relied upon in this judgment"}
              </p>
              <div className="mt-4 space-y-3" data-testid="provisions-list">
                {j.relevant_provisions.map((p, i) => (
                  <div key={i} className="rounded-xl border bg-card p-4" data-testid={`provision-item-${i}`}>
                    <div className="flex flex-wrap items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                        <Gavel className="h-4 w-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/updates?provision=${encodeURIComponent(p.sections ? `${p.sections},${p.act_name}` : p.act_name)}`}
                          className="inline-flex flex-wrap items-center gap-1.5 font-semibold text-foreground underline-offset-2 hover:text-primary hover:underline"
                          rel="tag"
                          data-testid={`provision-tag-${i}`}
                        >
                          <span className="font-mono text-sm">{p.sections ? `\u2018${p.sections}, ${p.act_name}\u2019` : `\u2018${p.act_name}\u2019`}</span>
                        </Link>
                        {p.description_en && (
                          <blockquote
                            cite={p.act_name}
                            data-testid={`provision-quote-${i}`}
                            className="mt-1.5 border-l-2 border-primary/30 pl-3 text-sm italic text-muted-foreground leading-relaxed"
                          >
                            &ldquo;{lang === "hi" && p.description_hi ? p.description_hi : p.description_en}&rdquo;
                          </blockquote>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Photos */}
          {j.photos?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-serif text-xl font-bold">{t("photos")}</h2>
              <div className="mt-3 grid gap-5 sm:grid-cols-2">
                {j.photos.map((p) => (
                  <figure key={p.id} data-testid="judgment-photo" className="overflow-hidden rounded-xl border bg-card">
                    <img src={imageUrl(p.url, { w: 800 })} alt={L(p, "caption") || j.case_title} loading="lazy" decoding="async" width={640} height={224} className="h-56 w-full object-cover" />
                    <figcaption data-testid="judgment-photo-caption" className="p-3 text-sm text-muted-foreground">{L(p, "caption")}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          )}

          {/* Full judgment text */}
          {j.full_text && <FullText text={j.full_text} caseTitle={j.case_title} lang={lang} />}

          {j.ai_generated && (
            <p className="mt-10 rounded-lg bg-secondary p-3 text-xs text-muted-foreground">{t("ai_summary_note")}</p>
          )}

          {(lang === "hi" ? j.key_quotes_hi : j.key_quotes_en)?.length ? (
            <div className="mt-8" data-testid="judgment-quotes">
              <h2 className="mb-3 flex items-center gap-2 font-serif text-xl font-bold"><Scale className="h-5 w-5 text-primary" /> {lang === "hi" ? "न्यायालय के शब्दों में" : "In the Court's words"}</h2>
              <div className="space-y-3">
                {(lang === "hi" ? j.key_quotes_hi : j.key_quotes_en).map((q, i) => (
                  <blockquote key={`q-${i}`} className="rounded-r-lg border-l-4 border-primary bg-card p-4 text-sm italic text-foreground/90">&ldquo;{q}&rdquo;</blockquote>
                ))}
              </div>
            </div>
          ) : null}

          {(() => {
            const area = (j.area_of_law || "").toLowerCase();
            let copy;
            if (area.includes("property") || area.includes("rent") || area.includes("land") || area.includes("eviction")) {
              copy = lang === "hi"
                ? "संपत्ति, बेदखली या मकान-मालिक/किरायेदार विवाद का सामना कर रहे हैं? निःशुल्क प्रारंभिक समीक्षा हेतु हमारे संपत्ति वकीलों से परामर्श करें।"
                : "Facing a property, eviction, or landlord-tenant dispute? Consult our property lawyers for a free initial review.";
            } else if (area.includes("motor") || area.includes("accident") || area.includes("insurance")) {
              copy = lang === "hi"
                ? "मोटर वाहन अधिनियम के तहत मुआवज़ा पाने में मदद चाहिए? हमारे दुर्घटना दावा अधिवक्ता मार्गदर्शन कर सकते हैं।"
                : "Need help securing compensation under the Motor Vehicles Act? Our accident claims advocates can guide you.";
            } else {
              copy = lang === "hi"
                ? "सुप्रीम कोर्ट या हाई कोर्ट में इसी तरह की कानूनी चुनौती? आज ही हमारे प्रो बोनो स्वयंसेवी अधिवक्ताओं से जुड़ें।"
                : "Facing a similar legal challenge in the Supreme Court or High Court? Connect with our pro bono volunteer advocates today.";
            }
            return (
              <div className="mt-10 rounded-2xl border border-primary/30 bg-primary/5 p-6" data-testid="conversion-widget">
                <h3 className="font-serif text-xl font-bold">{lang === "hi" ? "क्या आपको मदद चाहिए?" : "Need help with your case?"}</h3>
                <p className="mt-2 text-muted-foreground">{copy}</p>
                {(() => {
                  const rel = relatedPractices(j);
                  if (!rel.length) return null;
                  return (
                    <div className="mt-4" data-testid="related-practice-links">
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                        {lang === "hi" ? "संबंधित सुप्रीम कोर्ट सेवाएँ" : "Related Supreme Court services"}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {rel.map((p) => (
                          <Link
                            key={p.slug}
                            to={`/supreme-court-lawyer-for/${p.slug}`}
                            className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-sm font-medium transition-colors hover:border-primary/40 hover:text-primary"
                            data-testid={`related-practice-${p.slug}`}
                          >
                            <Scale className="h-3.5 w-3.5" /> {p.shortTitle}
                          </Link>
                        ))}
                        <Link
                          to="/supreme-court-lawyers"
                          className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-sm font-medium transition-colors hover:border-primary/40 hover:text-primary"
                          data-testid="related-all-lawyers"
                        >
                          <Users className="h-3.5 w-3.5" /> {lang === "hi" ? "सभी वकील" : "All Supreme Court lawyers"}
                        </Link>
                      </div>
                    </div>
                  );
                })()}
                <Button asChild className="mt-4 grad-cta text-white" data-testid="conversion-cta">
                  <Link to={`/free-legal-help?ref=${j.slug || j.id}`}>{lang === "hi" ? "निःशुल्क कॉलबैक का अनुरोध करें" : "Request Free Callback"}</Link>
                </Button>
              </div>
            );
          })()}

          {/* Related Questions of Law + matching advocates */}
          <RelatedQuestionsOfLaw j={j} lang={lang} />
        </div>

        {/* Metadata sidebar */}
        <aside className="h-fit rounded-xl border bg-card p-5 lg:sticky lg:top-20">
          <h3 className="font-serif text-lg font-semibold">{t("metadata")}</h3>
          <dl className="mt-4 space-y-4 text-sm">
            {j.citation && (
              <div className="flex gap-3"><Hash className="h-4 w-4 shrink-0 text-primary" /><div><dt className="text-xs text-muted-foreground">{t("citation")}</dt><dd className="font-mono">{j.citation}</dd></div></div>
            )}
            {j.judgment_date && (
              <div className="flex gap-3"><Calendar className="h-4 w-4 shrink-0 text-primary" /><div><dt className="text-xs text-muted-foreground">{t("date")}</dt><dd>{j.judgment_date}</dd></div></div>
            )}
            {j.area_of_law && (
              <div className="flex gap-3"><Scale className="h-4 w-4 shrink-0 text-primary" /><div><dt className="text-xs text-muted-foreground">{t("area_of_law")}</dt><dd>{j.area_of_law}</dd></div></div>
            )}
            {j.bench?.length > 0 && (
              <div className="flex gap-3"><Users className="h-4 w-4 shrink-0 text-primary" /><div><dt className="text-xs text-muted-foreground">{t("bench")}</dt><dd>{j.bench.join(", ")}</dd></div></div>
            )}
          </dl>
          {j.keywords?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {j.keywords.map((k) => <Badge key={k} variant="outline" className="text-xs">{k}</Badge>)}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
